import {useCallback} from 'react';
import {UnstyledButton} from '@shopify/polaris';
import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';

import styles from './Toggle.scss';

export interface ToggleProps {
  /** Indicates the ID of the element that is controlled by the toggle */
  ariaControls?: string;
  /** Indicates the ID of the element that describes the toggle */
  ariaDescribedBy?: string;
  /** Disable toggle */
  disabled?: boolean;
  /** A unique identifier for the toggle */
  id?: string;
  /** Aria-label for the toggle */
  ariaLabel: string;
  /** Name for form input */
  name?: string;
  /** Toggle is enabled */
  on?: boolean;
  /** Callback when focus is removed */
  onBlur?: () => void;
  /** Callback when the toggle is changed */
  onChange?: (newOn: boolean, id: string) => void;
  /** Callback when toggle is focused */
  onFocus?: () => void;
}

function Knob({on, disabled}: Pick<ToggleProps, 'on' | 'disabled'>) {
  const className = classNames(
    styles.Knob,
    on && styles.Knob__on,
    disabled && styles.Knob__disabled,
  );

  return <div className={className} />;
}

export function Toggle({
  ariaControls,
  ariaDescribedBy,
  disabled,
  id: idProp,
  name,
  onBlur,
  onChange,
  onFocus,
  on = false,
  ariaLabel,
}: ToggleProps) {
  const uniqId = uniqueId();
  const id = idProp ?? uniqId;

  const handleOnBlur = () => {
    onBlur?.();
  };

  const handleOnFocus = () => {
    onFocus?.();
  };

  const handleOnClick = useCallback(() => {
    const newOn = !on;

    onChange?.(newOn, id);
  }, [onChange, on, id]);

  const className = classNames(
    styles.Track,
    on && styles.Track__on,
    disabled && styles.Track__disabled,
  );

  return (
    <UnstyledButton
      aria-checked={on}
      aria-label={ariaLabel}
      ariaControls={ariaControls}
      ariaDescribedBy={ariaDescribedBy}
      className={className}
      disabled={disabled}
      id={id}
      name={name}
      onBlur={handleOnBlur}
      onClick={handleOnClick}
      onFocus={handleOnFocus}
      role="switch"
    >
      <Knob on={on} disabled={disabled} />
    </UnstyledButton>
  );
}
